import BackrsFilledDropShadow from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Fade, TextField, Typography, Theme } from '@mui/material';

import { FooterCard } from './FooterCard';
import { ProfilePopupWidget } from './ProfilePopupWidget';
import { SIDEBAR_FOOTER_CONTENT_WIDTH, SIDEBAR_STRINGS } from './sidebar.constants';
import {
  backrsLogoImageSx,
  drawerContentContainerSx,
  expandedSearchIconAdornmentSx,
  logoContainerSx,
  logoTextContainerSx,
  profileWidgetContainerSx,
  searchBarSx,
  sectionBottomBorderSx,
  sidebarSectionSx,
  sidebarSectionsContainerSx,
} from './sidebar.styles';
import { SidebarMenu } from './sidebar.types';
import { SidebarItem } from './SidebarItem';

type SidebarInnerProps = Readonly<{
  sidebar: SidebarMenu;
  sidebarOpen: boolean;
  isMobile: boolean;
  isEmulating: boolean;
  expandedItem?: string;
  setExpandedItem: (title?: string, shouldOpenSidebar?: boolean) => void;
  handleToggleDesktop: () => void;
  handleCloseDesktop: () => void;
  handleCloseMobile: () => void;
  handleSearchEnterKey: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}>;

export const SidebarInner = ({
  sidebar,
  sidebarOpen,
  isMobile,
  isEmulating,
  expandedItem,
  setExpandedItem,
  handleToggleDesktop,
  handleCloseDesktop,
  handleCloseMobile,
  handleSearchEnterKey,
}: SidebarInnerProps) => {
  const unAuthUser = sidebar.user?.isUnauthenticated;

  const content = (
    <Box sx={drawerContentContainerSx(sidebarOpen)}>
      {/* Header */}
      <Box sx={logoContainerSx}>
        <Box
          component="img"
          src={BackrsFilledDropShadow}
          alt="Backrs Logo"
          sx={backrsLogoImageSx(sidebarOpen)}
          onClick={() => {
            if (isMobile) handleCloseMobile();
            else handleToggleDesktop();
          }}
        />
        <Box sx={logoTextContainerSx}>
          <Typography variant="display-xs-semibold">{SIDEBAR_STRINGS.backrs}</Typography>
        </Box>
      </Box>

      {/* Search */}
      {!sidebar.hideSearch ? (
        <TextField
          id="sidebar-search-input"
          variant="outlined"
          placeholder={SIDEBAR_STRINGS.search}
          sx={searchBarSx(!isMobile ? sidebarOpen : true, sidebar.search.isPageActive)}
          onClick={!isMobile && !sidebarOpen ? () => sidebar.search.onSearch() : undefined}
          onKeyDown={handleSearchEnterKey}
          InputProps={{
            startAdornment: (
              <Box sx={expandedSearchIconAdornmentSx(!isMobile ? sidebarOpen : true)}>
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Background/bg-primary-solid"
                />
              </Box>
            ),
          }}
        />
      ) : null}

      {/* Sections */}
      <Box sx={sidebarSectionsContainerSx}>
        {sidebar.sections.map((section, i) => (
          <Box key={i} sx={sidebarSectionSx}>
            {section.menuItems.map((item, j) => (
              <SidebarItem
                key={`${i}-${j}`}
                sidebarMenuItem={item}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                isSidebarOpen={isMobile ? true : sidebarOpen}
                setIsSideBarOpen={() => {
                  if (isMobile) {
                    handleCloseMobile();

                    return;
                  }

                  handleCloseDesktop();
                }}
              />
            ))}
            {i !== sidebar.sections.length - 1 && !unAuthUser && <Box sx={sectionBottomBorderSx} />}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const footer = (
    <Box sx={profileWidgetContainerSx(!isMobile ? sidebarOpen : true)}>
      {/* 300ms matches the sidebar's width transition; unmounting keeps a clipped sliver of the
          card out of the collapsed rail. */}
      {sidebar.footerCard && (
        <Fade in={isMobile || sidebarOpen} timeout={300} unmountOnExit>
          <Box
            sx={{
              marginBottom: (theme: Theme) => theme.backrs.spacing['spacing-2xl'],
              width: isMobile ? undefined : SIDEBAR_FOOTER_CONTENT_WIDTH,
            }}
          >
            <FooterCard card={sidebar.footerCard} />
          </Box>
        </Fade>
      )}

      <ProfilePopupWidget
        backrsUser={sidebar.user}
        isSidebarOpen={!isMobile ? sidebarOpen : true}
        isEmulating={isEmulating}
        topSectionItems={sidebar.accountMenuItems.topSectionItems}
        bottomSectionItems={sidebar.accountMenuItems.bottomSectionItems}
        handleCloseSidebar={() => {
          if (isMobile) {
            handleCloseMobile();

            return;
          }

          handleCloseDesktop();
        }}
      />
    </Box>
  );

  return (
    <>
      {content}
      {footer}
    </>
  );
};
